import React from 'react';
import { validateCui } from 'services/businessBilling/cui';

export const CUI_LOOKUP_DEBOUNCE_MS = 500;

export type DebouncedCui =
    | { status: 'idle' } // Field empty: nothing to show, nothing to do.
    | { status: 'waiting' } // Typed, but not yet quiet for the delay: no message, no lookup, no submit.
    | { status: 'invalid'; code: 'CUI_INVALID_FORMAT' } // Quiet and invalid: show the inline message; BOLT is never called.
    | { status: 'ready'; cui: string }; // Quiet and valid: the caller may look up exactly this CUI.

/**
 * Waits until `raw` has been unchanged for `delayMs` before judging it. The
 * status is derived on every render, so a changed value is `waiting` in the
 * very render it changes: an older `ready` can never be seen for a new value.
 * A change of `resetKey` (session / journey) counts as new typing.
 */
export const useDebouncedCui = (
    raw: string,
    delayMs: number = CUI_LOOKUP_DEBOUNCE_MS,
    resetKey: string = '',
): DebouncedCui => {
    const [settled, setSettled] = React.useState<{ raw: string; key: string } | null>(null);

    React.useEffect(() => {
        setSettled(null);
        if (raw === '') {
            return undefined;
        }
        const timer = setTimeout(() => setSettled({ raw, key: resetKey }), delayMs);
        return () => clearTimeout(timer);
    }, [raw, delayMs, resetKey]);

    if (raw === '') {
        return { status: 'idle' };
    }
    if (!settled || settled.raw !== raw || settled.key !== resetKey) {
        return { status: 'waiting' };
    }
    const validation = validateCui(raw);
    return validation.status === 'valid'
        ? { status: 'ready', cui: validation.cui }
        : { status: 'invalid', code: 'CUI_INVALID_FORMAT' };
};
